import { getCookieData } from '@repo/common-utils';
import { useWindowOpen } from '@repo/platform';
import { useToast } from '@zenterprise-inc/ui';
import { useSetAtom } from 'jotai';
import { useRouter } from 'next/router';
import { useState } from 'react';

import { CHECK_DECISION_ACTION_CODE_COOKIE_KEY, setResultRefundData } from '@/components/tax-refund/common/data/result-refund-data';
import { getIndisJointCertificateErrorContent } from '@/components/tax-refund/common/utils/getJointCertificateErrorContent';
import JointCertificateSelectView from '@/components/tax-refund/common/view/JointCertificateSelectView';
import { ErrorDialogContent } from '@/components/tax-refund/individual/JointCertificate/select/IndisSelectJoinCertificateErrorDialog';
import { 홈택스공동인증서등록가이드주소, 홈택스아이디생성주소 } from '@/lib/constants/link';
import { 환급결정확인알림톡_PATH } from '@/lib/constants/paths';
import { useHometaxLogin } from '@/lib/hooks/api/mutation/use-hometax-login';
import useSaveRefundActionLog from '@/lib/hooks/api/mutation/use-save-refund-action-log';
import { refundTokenAtom } from '@/lib/stores/simple-auth';

const ResultJointCertificateSelectContent = () => {
  const router = useRouter();
  const toast = useToast();

  const { commit: commitHometaxLogin, isInFlight: isCheckJoinCertiLoading } = useHometaxLogin();
  const { commit: saveActionLog } = useSaveRefundActionLog();
  const [isOpenErrorDialog, setIsOpenErrorDialog] = useState(false);
  const [errorIsOpenSelectAfterHandlePrimaryButton, setErrorIsOpenSelectAfterHandlePrimaryButton] = useState(false);
  const [errorDialogContent, setErrorDialogContent] = useState<ErrorDialogContent | null>(null);
  const { openWindow } = useWindowOpen();
  const setRefundToken = useSetAtom(refundTokenAtom);

  const loginHometax = (data: { signCert: string; signPri: string; signPw: string }) => {
    commitHometaxLogin({
      variables: {
        corpType: 'INDIS',
        loginType: 'CommonCert',
        certificate: {
          signCert: data.signCert,
          signPri: data.signPri,
          signPw: data.signPw
        }
      },
      onCompleted: (response, error) => {
        if (error) {
          console.log(error);
          if (error.length > 0) {
            toast({ content: error[0]?.message, icon: 'error', iconColor: 'red.600' });
          }
          return;
        }

        const { hometaxLogin } = response || {};
        if (!hometaxLogin) {
          console.error('Response object is empty.');
          return;
        }
        const { result, refundToken, errors } = hometaxLogin;
        const { code } = errors ?? {};
        if (result && refundToken) {
          const actionCode = router.query.actionCode ?? getCookieData(CHECK_DECISION_ACTION_CODE_COOKIE_KEY);
          if (actionCode) {
            saveActionLog({ variables: { actionCode, actionStatus: '공동인증서완료' } });
          }
          setResultRefundData({
            authType: 'joint-certificate',
            corpType: 'INDIS'
          });
          setRefundToken(refundToken);

          router.push({
            pathname: `${환급결정확인알림톡_PATH.환급결정로딩}`,
            query: router.query
          });
          return;
        }
        if (code) {
          setErrorDialogContent(getIndisJointCertificateErrorContent(code));
          setIsOpenErrorDialog(true);
          return;
        }
        console.error(`Unknown error: ${code}`);
      },
      onError: (error) => {
        console.log(error);
        toast({ content: '인증서로 홈택스 로그인 확인 중 에러 발생', icon: 'error', iconColor: 'red.600' });
      }
    });
  };

  const moveJointCertificateInstallPage = () => {
    router.push({
      pathname: `${환급결정확인알림톡_PATH.공동인증서설치판단}`,
      query: router?.query
    });
  };

  const errorHandleClickPrimaryButton = () => {
    switch (errorDialogContent?.errorCode) {
      case 'ERR_HOMETAX_NO_JUMIN_ID':
      case 'ERR_HOMETAX_EXPIRE_CERT':
        setIsOpenErrorDialog(false);
        setErrorIsOpenSelectAfterHandlePrimaryButton(true);
        break;

      case 'ERR_HOMETAX_NO_REGIST_CERT':
        openWindow(홈택스공동인증서등록가이드주소);
        break;

      case 'ERR_HOMETAX_NO_USER':
        openWindow(홈택스아이디생성주소);
        break;

      default:
        break;
    }
  };

  const errorHandleClickSecondaryButton = () => {
    setIsOpenErrorDialog(false);
  };

  return (
    <JointCertificateSelectView
      viewController={{
        isCheckJoinCertiLoading,
        loginHometax,
        isOpenErrorDialog,
        moveJointCertificateInstallPage,
        errorTitle: errorDialogContent?.title ?? '',
        errorMessage: errorDialogContent?.message ?? '',
        errorPrimaryButton: errorDialogContent?.primaryButtonText ?? '',
        errorSecondaryButton: errorDialogContent?.secondaryButtonText ?? '',
        errorIsOpenSelectAfterHandlePrimaryButton,
        errorHandleClickPrimaryButton,
        errorHandleClickSecondaryButton,
        shouldSaveActionLog: true
      }}
    />
  );
};

export default ResultJointCertificateSelectContent;
